import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CompaniesService } from '../../../core/api/companies.service';
import { PageCompaniesResponse } from '../../../core/model/pageCompaniesResponse';
import { GetCompaniesQueryResponse } from '../../../core/model/getCompaniesQueryResponse';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomCompanyService } from '@core/services/company.service';
import { TranslateService } from '@ngx-translate/core';
import { UserProfileService } from '@core/services/user-profile.service';
import { ThemeService } from '@core/services/theme.service';
import { MatDialog } from '@angular/material/dialog';
import { NewCompanyDialogComponent } from '../../../modules/administration/dialogs/new-company-dialog/new-company-dialog.component';

@Component({
  selector: 'app-change-company',
  templateUrl: './change-company.component.html',
  styleUrl: './change-company.component.scss',
  standalone: false
})
export class ChangeCompanyComponent implements OnInit, OnDestroy {
  searchText: string = '';
  companies: GetCompaniesQueryResponse[] = [];
  filteredCompanies: GetCompaniesQueryResponse[] = [];
  isLoading: boolean = false;
  hasMoreData: boolean = false;
  continuationToken: string = '';
  selectedIndex: number = -1;

  private searchSubject = new Subject<string>();

  constructor(
    private dialogRef: MatDialogRef<ChangeCompanyComponent>,
    private companiesService: CompaniesService,
    private oauthService: OAuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private companyService: CustomCompanyService,
    private translate: TranslateService,
    private userProfileService: UserProfileService,
    private themeService: ThemeService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    // Configurar debounce para la búsqueda
    this.searchSubject.pipe(
      debounceTime(300), // Esperar 300ms después del último cambio
      distinctUntilChanged() // Solo si el valor cambió
    ).subscribe(searchTerm => {
      this.resetSearch();
      this.loadCompanies(searchTerm);
    });

    // Cargar empresas inicial
    this.loadCompanies();
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }

  private resetSearch() {
    this.companies = [];
    this.filteredCompanies = [];
    this.continuationToken = '';
    this.hasMoreData = false;
    this.selectedIndex = -1;
  }

  private loadCompanies(searchTerm?: string) {
    this.isLoading = true;
    const searchText = searchTerm !== undefined ? searchTerm : this.searchText;

    this.companiesService.companiesPaginatedActive(
      30,
      searchText.trim() || undefined,
      this.continuationToken || undefined
    ).subscribe({
      next: (response: PageCompaniesResponse) => {
        const newCompanies = response.companies || [];
        this.companies = [...this.companies, ...newCompanies];
        this.filteredCompanies = [...this.companies];
        this.continuationToken = response.continuationToken ? JSON.stringify(response.continuationToken) : '';
        this.hasMoreData = !!response.continuationToken;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.isLoading = false;
      }
    });
  }

  onScroll(event: any): void {
    const scrollPosition = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const clientHeight = event.target.clientHeight;

    // Cargar más datos cuando estemos cerca del final (10px antes del final)
    if (scrollPosition + clientHeight >= scrollHeight - 10) {
      this.onLoadMoreCompanies();
    }
  }

  onLoadMoreCompanies(): void {
    if (this.isLoading || !this.hasMoreData) return;
    this.loadCompanies(this.searchText);
  }

  close() {
    this.dialogRef.close();
  }

  changeCompany(company: GetCompaniesQueryResponse) {
    this.companiesService.v1CompaniesApiUsersSwitchCompanyCompanyIdPut(company.id!)
      .subscribe({
        next: async (response) => {

          this.snackBar.open(
            this.translate.instant('NAVIGATION.DIALOGS.CHANGE-COMPANY.MESSAGES.SUCCESS'),
            this.translate.instant('NAVIGATION.DIALOGS.CHANGE-COMPANY.ACTIONS.CLOSE'),
            {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['success-snackbar']
            }
          );


          try {

            // this.oauthService.logOut(true);
            // await this.oauthService.loadDiscoveryDocument();

            // this.companyService.clearCompanyProfile();
            // this.userProfileService.clearUserModules();
            // this.oauthService.initCodeFlow('/home');

            // Resetear tema a valores por defecto antes de cambiar empresa
            this.themeService.resetTheme();

            // Limpia tokens, pero NO revoca la sesión en el Identity Provider
            this.oauthService.logOut(false);
            this.companyService.clearCompanyProfile();
            this.userProfileService.clearUserModules();
            this.broadcastCompanySwitch(company.id!);

            await this.oauthService.loadDiscoveryDocument();

            // Inicia un nuevo code flow: usuario no mete credenciales
            this.oauthService.initCodeFlow('/home');

          } catch (error) {
            console.error('Error during token refresh:', error);
            this.oauthService.initCodeFlow('/home');
          }

        },
        error: (error) => {
          console.error('Error switching company:', error);

          this.snackBar.open(
            this.translate.instant('NAVIGATION.DIALOGS.CHANGE-COMPANY.MESSAGES.ERROR'),
            this.translate.instant('NAVIGATION.DIALOGS.CHANGE-COMPANY.ACTIONS.CLOSE'),
            {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );

        }
      });
  }

  filterCompanies() {
    this.searchSubject.next(this.searchText);
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.filteredCompanies.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredCompanies.length - 1);
        this.scrollToSelected();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.scrollToSelected();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredCompanies.length) {
          this.changeCompany(this.filteredCompanies[this.selectedIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  }

  private scrollToSelected() {
    // Asegurar que el elemento seleccionado esté visible en el scroll
    setTimeout(() => {
      const container = document.querySelector('.company-list-container');
      const selectedElement = document.querySelector('.company-item.keyboard-selected');
      if (container && selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  onMouseEnter(index: number) {
    this.selectedIndex = index;
  }

  private broadcastCompanySwitch(companyId: string) {
    // Notifica a otras pestañas que se ha cambiado de empresa
    localStorage.setItem('company-switch', JSON.stringify({
      companyId,
      ts: Date.now()
    }));
  }

  openNewCompanyDialog(): void {
    this.dialogRef.close();
    this.dialog.open(NewCompanyDialogComponent, {
      width: '1000px',
      maxWidth: '90vw',
      disableClose: true
    });
  }
}
